import type { Locale, MeContextResponse, Task } from '@saniplus/contracts';
import {
  Clock,
  MapPin,
  Notice,
  NullValue,
  RefreshCw,
  Skeleton,
  TriangleAlert,
  WifiOff,
  decorativeIcon,
} from '@saniplus/ui';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { HomeView, QueueState } from '../offline';
import { HomeTasksLine, PunchOutGate, blockingTasks } from '../tasks';
import { FactList, type Fact } from './components/FactList';
import { InstrumentPanel, type InstrumentPanelProps } from './components/InstrumentPanel';
import { PunchConfirmSheet } from './components/PunchConfirmSheet';
import { PunchKey } from './components/PunchKey';
import { ReconciliationPanel } from './components/ReconciliationPanel';
import { ShiftLine } from './components/ShiftLine';
import { SupervisorSheet } from './components/SupervisorSheet';
import {
  clockParts,
  dayNumber,
  elapsedParts,
  longDate,
  minutesOf,
  monthName,
  msUntil,
  relativeDay,
  siteTimezoneOf,
  zoneAbbreviation,
} from './lib/format';

/**
 * W3 — the home screen: **one panel, one key, one quiet line.**
 *
 * ## One flowing column (C-19, 2026-09-03)
 *
 * The screen is a single top-to-bottom column that scrolls with the shell's
 * `main` — panel, tasks, key, supervisor — with **no fixed thumb zone and no
 * inner scroller**. The previous shape (a `min-h-0 flex-1` scroller above a
 * `shrink-0` footer) pinned the key to the bottom and gave the on-duty card
 * its own scrollbar on a real phone, where the browser's URL bar and toolbars
 * leave ~630 px of the 844: the card was clipped mid-fact while dead canvas
 * sat under « Contacter mon superviseur ». On a device report (iPhone 12,
 * 2026-09-03) that read as a rendering fault, so the vertical contract is
 * withdrawn for this screen: nothing is pinned, nothing is clipped, and the
 * one scroll is the page's own — the affordance every phone user already has.
 *
 * There is still **exactly one panel in every state**: the instrument, with a
 * footer that changes — the shift on a working screen, the blocking facts on a
 * stale-open one, the week ahead when there is no shift. « se termine demain »
 * appears only when the shift measurably crosses midnight (C-14).
 *
 * ## The two ranks
 *
 * When there is a punch to make, the punch key is the primary and the
 * supervisor is a quiet line beneath it. When there is **no** key — a stale
 * open punch, a window that has closed, no shift tonight — the supervisor *is*
 * the way forward and is drawn as the key. Every worker dead end ends in a
 * person; no working screen has two primaries.
 *
 * ## The second tap
 *
 * The key no longer punches. It opens `PunchConfirmSheet`, which states what
 * is about to be recorded and offers an equally reachable Cancel. A punch is
 * irreversible — only a supervisor can correct one, with a written reason — so
 * the 88 px target that makes it easy to hit deliberately also makes it easy
 * to hit by accident, and that is the one place this product owes a
 * confirmation.
 *
 * Nothing here decides anything. `features/offline` owns the state, the clock
 * and the queue; the server owns authorization. This owns the pixels.
 */
export interface HomeScreenProps {
  readonly home: HomeView;
  readonly context: MeContextResponse | null;
  readonly queue: QueueState;
  readonly loading: boolean;
  readonly busy: boolean;
  readonly now: number;
  readonly locale: Locale;
  /**
   * The device clock at the tap the server has just confirmed. The queue row is
   * gone by then, so « Heure retenue » gets it from the caller or shows no
   * strike-through at all — it never invents one.
   */
  readonly confirmedDeviceAt?: string | null;
  readonly onPunchIn: () => void;
  readonly onPunchOut: () => void;
  /** Kept for the shell's route table; the screen no longer draws a link to it. */
  readonly shiftsPath?: string;
  /**
   * Phase 5 / P3 — the PENDING tasks of the current-or-next shift, composed
   * in from `PunchPage` (`useTaskGate`) and never fetched here.
   *
   * The rows, not a count: T-14's home line shows the first two TITLES
   * (2026-09-03 device report — a worker seeing « 1 tâche à faire » on the
   * gated key had to punch out to find out which task it was). How many of
   * them `blocks_punch_out` — the UI half of T-07, the server guard is P4 —
   * is derived from this same array by `blockingTasks()`, so the list and
   * the gate can never disagree. Defaults to empty, so a caller that has not
   * wired tasks yet — a test, mainly — renders exactly as before.
   */
  readonly tasks?: readonly Task[];
  /** The override punch OUT (T-07): rejects to keep its sheet open. */
  readonly onOverridePunchOut?: (reason: string) => Promise<void>;
}

/**
 * The site whose timezone every instant on this screen renders in.
 *
 * The home view's own shift wins — it is the one the panel is about — and the
 * context chain behind it is `siteTimezoneOf`, shared with the recovery screen
 * and the shell so all three answer the same zone for the same session.
 */
function timezoneOf(context: MeContextResponse | null, home: HomeView): string {
  return (
    home.shift?.site.timezone ??
    home.openSession?.site.timezone ??
    home.nextShift?.site.timezone ??
    siteTimezoneOf(context)
  );
}

/**
 * Which punch the ONE key offers, or `null` when there is no punch to make.
 *
 * A queued IN with no server session still means "you are working": OUT is
 * never refused (D-08), so the key stays live rather than stranding a worker
 * whose entry has not synced yet.
 */
function nextAction(home: HomeView, queue: QueueState): 'in' | 'out' | null {
  // D-08's hard block: a session left open on a previous shift is closed by a
  // supervisor with a written reason, never by a punch now — an OUT tonight
  // would write the wrong hours onto the wrong night.
  if (home.state === 'BLOCKED_STALE_OPEN') return null;
  if (queue.pending.some((item) => item.type === 'out')) return null;
  if (home.openSession !== null) return 'out';
  if (queue.pending.some((item) => item.type === 'in')) return 'out';
  if (home.state === 'NOT_PUNCHED_IN_WINDOW_OPEN') return 'in';
  return null;
}

export function HomeScreen({
  home,
  context,
  queue,
  loading,
  busy,
  now,
  locale,
  confirmedDeviceAt = null,
  onPunchIn,
  onPunchOut,
  tasks = [],
  onOverridePunchOut,
}: HomeScreenProps) {
  const { t } = useTranslation('punch');
  const taskBlockingCount = blockingTasks(tasks).length;
  const zone = timezoneOf(context, home);
  const [confirming, setConfirming] = useState<'in' | 'out' | null>(null);

  const clock = (iso: string): string => {
    const parts = clockParts(iso, zone);
    return parts === null ? '' : t('format.clock', parts);
  };
  const duration = (ms: number): string => t('format.duration', elapsedParts(ms));
  const durationSr = (ms: number): string => {
    const { hours, minutes } = elapsedParts(ms);
    return t('a11y.durationHm', { hours: Number(hours), minutes: Number(minutes) });
  };
  const span = (ms: number): string => {
    const total = minutesOf(ms);
    return total >= 60 ? duration(ms) : t('format.minutes', { count: total });
  };
  /** « Demain, vendredi 28 août » — the same phrasing `ShiftLine` uses. */
  const dayLabel = (iso: string, tz: string): string => {
    const date = longDate(iso, tz, locale);
    const relative = relativeDay(iso, tz, now);
    if (relative === 'today') return t('shift.dateRelative', { relative: t('shift.today'), date });
    if (relative === 'tomorrow') {
      return t('shift.dateRelative', { relative: t('shift.tomorrow'), date });
    }
    return date;
  };

  if (loading && context === null) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label={t('home.loading')}>
        <Skeleton width="w-full" height="h-56" rounded="control" />
        <Skeleton width="w-full" height="h-touch-lg" rounded="control" />
      </div>
    );
  }

  // ── the instrument ───────────────────────────────────────────────────────
  const instrument = ((): InstrumentPanelProps => {
    const session = home.openSession;
    const shift = home.shift;

    if (home.state === 'PUNCHED_IN' || home.state === 'CONFIRMED') {
      if (session !== null) {
        const elapsed = home.elapsedMs;
        // The site now has a line of its own in the footer, so the footnote
        // says only the thing the footer cannot: which clock this number is
        // measured on.
        const workedFootnote = t('footnote.worked', {
          zone: zoneAbbreviation(session.openedAt, zone, locale),
        });
        // DESIGN.md: « a missing value is — in danger ink, never 0 h 00 ». An
        // unparseable `openedAt` is "nobody knows how long you have worked",
        // and printing « 0 h 00 » in success tone says the opposite in the one
        // place the worker trusts most.
        if (elapsed === null) {
          return {
            tone: 'warning',
            word: t('state.onDutyUnknown'),
            cap: t('cap.since', { time: clock(session.openedAt) }),
            value: <NullValue label={t('a11y.elapsedUnknown')} />,
            valueScale: 'duration',
            footnote: workedFootnote,
          };
        }
        return {
          tone: 'success',
          word: t('state.onDuty'),
          cap: t('cap.since', { time: clock(session.openedAt) }),
          value: duration(elapsed),
          valueScale: 'duration',
          valueSrLabel: durationSr(elapsed),
          footnote: workedFootnote,
        };
      }
      const confirmed = queue.lastConfirmed;
      return {
        tone: 'neutral',
        word: t('state.offDuty'),
        value: confirmed === null ? '' : clock(confirmed.recordedAt),
        valueScale: 'clock',
        valueUnit: t('cap.captured'),
        ...(confirmed === null ? {} : { valueDateTime: confirmed.recordedAt }),
      };
    }

    if (home.state === 'OFFLINE_QUEUED') {
      const queued = home.queuedPunch;
      const elapsed = home.elapsedMs;
      return {
        tone: 'warning',
        /*
         * NEVER « Hors service » here. `nextAction()` keeps OUT live while an
         * IN is only queued (D-08), so an off-duty word beside a « Pointer la
         * sortie » key tells a worker they never punched in — and they punch
         * in again. A duplicate punch is a payroll defect. The state is the
         * QUEUE's, so the word is the queue's: « En attente d'envoi ».
         */
        word: t('state.queued'),
        ...(queued === null ? {} : { cap: clock(queued.deviceRecordedAt) }),
        value:
          elapsed === null ? (queued === null ? '' : clock(queued.deviceRecordedAt)) : duration(elapsed),
        valueScale: elapsed === null ? 'clock' : 'duration',
        ...(elapsed === null ? {} : { valueSrLabel: durationSr(elapsed) }),
        footnote: t('footnote.queued'),
        footnoteIcon: <WifiOff {...decorativeIcon('sm')} />,
        footnoteTone: 'warning',
      };
    }

    if (home.state === 'BLOCKED_STALE_OPEN') {
      const openedAt = session?.openedAt ?? null;
      return {
        tone: 'danger',
        word: t('state.blocked'),
        cap: t('cap.stayedOpen'),
        value: openedAt === null ? '' : dayNumber(openedAt, zone),
        valueScale: 'duration',
        ...(openedAt === null ? {} : { valueUnit: monthName(openedAt, zone, locale) }),
        footnote: t('footnote.blocked', {
          date: openedAt === null ? '' : longDate(openedAt, zone, locale),
        }),
        footnoteIcon: <TriangleAlert {...decorativeIcon('sm')} />,
        footnoteTone: 'danger',
      };
    }

    if (home.state === 'SUBMITTING') {
      const queued = home.queuedPunch;
      return {
        tone: 'info',
        word: t('state.submitting'),
        cap: t('cap.capturedOn'),
        value: queued === null ? '' : clock(queued.deviceRecordedAt),
        valueScale: 'clock',
        valueUnit: t('cap.captured'),
        footnote: t('footnote.submitting'),
        footnoteIcon: <RefreshCw {...decorativeIcon('sm')} />,
        footnoteTone: 'info',
      };
    }

    if (home.state === 'WINDOW_NOT_OPEN_YET') {
      const opensAt = home.windowOpensAt;
      const untilOpen = opensAt === null ? null : msUntil(opensAt, now);
      const closed = opensAt === null || untilOpen === null || untilOpen <= 0;
      return {
        tone: 'neutral',
        word: t('state.offDuty'),
        value: shift === null ? '' : clock(shift.startsAt),
        valueScale: 'clock',
        valueUnit: t('cap.start'),
        footnote:
          closed || opensAt === null || untilOpen === null
            ? t('footnote.windowClosed')
            : t('footnote.windowOpensAt', { time: clock(opensAt), duration: span(untilOpen) }),
        footnoteIcon: <Clock {...decorativeIcon('sm')} />,
        footnoteTone: closed ? 'danger' : 'warning',
      };
    }

    if (home.state === 'NOT_PUNCHED_IN_WINDOW_OPEN') {
      const untilStart = shift === null ? null : msUntil(shift.startsAt, now);
      return {
        tone: 'neutral',
        word: t('state.offDuty'),
        value: shift === null ? '' : clock(shift.startsAt),
        valueScale: 'clock',
        valueUnit: t('cap.start'),
        ...(untilStart === null
          ? {}
          : {
              footnote:
                untilStart >= 0
                  ? t('footnote.startsIn', { duration: span(untilStart) })
                  : t('footnote.startedAgo', { duration: span(-untilStart) }),
              footnoteIcon: <Clock {...decorativeIcon('sm')} />,
              footnoteTone: 'info' as const,
            }),
      };
    }

    /*
     * NO NUMBER HERE, on purpose.
     *
     * Every other state's readout is a time on the day the worker is standing
     * in, so the day needs no saying. The next shift is not: rendering its
     * start as this panel's one big number printed « 10 h 12 » with nothing
     * within three lines of it naming the day, and a time set that large is
     * read as today's. There is no number that is honest at that size here, so
     * there is none — the footer carries the answer, day first, under its own
     * label.
     */
    return {
      tone: 'neutral',
      word: t('state.noShift'),
      valueScale: 'clock',
      footnote: t('footnote.noShift'),
      footnoteIcon: <Clock {...decorativeIcon('sm')} />,
    };
  })();

  // ── the blocking facts, when the panel's footer is a refusal ─────────────
  const blockingFacts = (): Fact[] => {
    const session = home.openSession;
    if (session === null) return [];
    const shift = home.shift;
    const facts: Fact[] = [
      {
        key: 'nightOf',
        label: t('recovery.staleOpen.nightOf'),
        value: longDate(session.openedAt, zone, locale),
        prose: true,
      },
    ];
    if (shift !== null) {
      facts.push({
        key: 'shift',
        label: t('recovery.staleOpen.shift'),
        value: `${clock(shift.startsAt)} ${t('format.arrow')} ${clock(shift.endsAt)}`,
      });
    }
    facts.push(
      { key: 'in', label: t('recovery.staleOpen.punchedIn'), value: clock(session.openedAt) },
      {
        key: 'out',
        label: t('recovery.staleOpen.punchedOut'),
        value: null,
        nullLabel: t('recovery.staleOpen.noneLabel'),
      },
      {
        key: 'site',
        label: t('recovery.gpsDenied.site'),
        value: session.site.name,
        prose: true,
      },
    );
    return facts;
  };

  // ── the panel's footer: one panel per state, four footers ───────────────
  const blocked = home.state === 'BLOCKED_STALE_OPEN';
  const openSession = home.openSession;
  const contextShift = home.shift ?? home.nextShift;

  const panelFooter = ((): ReactNode => {
    if (blocked) {
      return (
        <>
          <FactList facts={blockingFacts()} />
          <p className="mt-2.5 border-t border-hairline pt-2.5 text-body text-ink-muted">
            {t('recovery.staleOpen.footnote')}
          </p>
        </>
      );
    }

    if (home.state === 'NO_SHIFT_TODAY') {
      const next = home.nextShift ?? context?.upcomingShifts[0] ?? null;
      if (next === null) {
        return <p className="text-body text-ink-muted">{t('recovery.noShift.empty')}</p>;
      }
      /*
       * The next shift in full, then the ones after it as a list.
       *
       * "You are not due today" is half an answer, and the half a worker
       * actually walked to their phone for is the other one. The next shift
       * gets the day at title weight with its window and site under it; the
       * days after are a plain date → window list, which is the same shape
       * « Mes quarts » uses, so the tab this screen sends them to looks like
       * what they just read.
       */
      const after = (context?.upcomingShifts ?? [])
        .filter((item) => item.id !== next.id)
        .slice(0, 3);
      return (
        <>
          <ShiftLine shift={next} locale={locale} now={now} emphasis="headline" />
          {after.length === 0 ? null : (
            <FactList
              className="mt-3 border-t border-hairline pt-2"
              facts={after.map((item) => ({
                key: item.id,
                label: dayLabel(item.startsAt, item.site.timezone),
                value: `${clock(item.startsAt)} ${t('format.arrow')} ${clock(item.endsAt)}`,
              }))}
            />
          )}
        </>
      );
    }

    if (contextShift !== null) {
      /* Off duty the instrument's 40 px number IS the shift's start, so the
         line drops it and says what the readout cannot: when it ends. */
      const startIsTheReadout =
        home.state === 'NOT_PUNCHED_IN_WINDOW_OPEN' || home.state === 'WINDOW_NOT_OPEN_YET';
      return (
        <ShiftLine
          shift={contextShift}
          locale={locale}
          now={now}
          omitStart={startIsTheReadout}
        />
      );
    }

    if (openSession !== null) {
      /* An open session whose shift the context no longer reports — a punch on
         an ended or unscheduled shift. The worker is on the clock somewhere,
         and the screen must still say WHERE. */
      return (
        <p className="flex items-start gap-2">
          <span aria-hidden="true" className="mt-0.5 shrink-0 text-ink-muted">
            <MapPin {...decorativeIcon('sm')} />
          </span>
          <span className="min-w-0 text-body text-ink">
            {openSession.site.name}
            <span aria-hidden="true" className="text-ink-muted">
              {' · '}
            </span>
            <span className="text-ink-muted">
              {openSession.zone === null ? t('shift.noZone') : openSession.zone.name}
            </span>
          </span>
        </p>
      );
    }

    return null;
  })();

  // ── the action slot ──────────────────────────────────────────────────────
  const action = nextAction(home, queue);
  const offline = home.state === 'OFFLINE_QUEUED';
  const opensAt = home.windowOpensAt;
  const untilOpen = opensAt === null ? null : msUntil(opensAt, now);
  const windowClosed = opensAt === null || untilOpen === null || untilOpen <= 0;

  const actionSlot = ((): ReactElement | null => {
    if (busy || home.state === 'SUBMITTING') {
      return (
        <PunchKey
          form="loading"
          type={action ?? 'in'}
          label={t('action.sending')}
          subLabel={t('action.sendingSub')}
        />
      );
    }

    if (home.state === 'WINDOW_NOT_OPEN_YET') {
      // A window that has already closed offers no key at all: the escape
      // hatch is a supervisor's `create_missing` adjustment (D-11).
      if (windowClosed || opensAt === null || untilOpen === null) return null;
      return (
        <PunchKey
          form="disabled"
          type="in"
          label={t('action.in')}
          subLabel={t('action.opensIn', { duration: span(untilOpen) })}
          note={t('action.opensAt', { time: clock(opensAt) })}
        />
      );
    }

    if (action === null) return null;

    // Phase 5 / P3, UI half of T-07: a blocking pending task replaces the
    // OUT key with the gated one — never `PunchKey`'s "disabled" form, whose
    // native `disabled` button cannot be tapped at all, and this one must be
    // (it opens the task list). The server guard lands in P4; today this is
    // read-only advice from `/me/tasks`, so it only ever adds a step, never
    // blocks a punch the server would have accepted.
    if (action === 'out' && taskBlockingCount > 0 && onOverridePunchOut !== undefined) {
      return <PunchOutGate blockingCount={taskBlockingCount} onOverride={onOverridePunchOut} />;
    }

    const start = home.shift?.startsAt ?? null;
    return (
      <PunchKey
        form="action"
        type={action}
        label={action === 'in' ? t('action.in') : t('action.out')}
        subLabel={
          offline
            ? action === 'in'
              ? t('action.inSubQueued')
              : t('action.outSubQueued')
            : action === 'in'
              ? t('action.inSub', { time: start === null ? '' : clock(start) })
              : t('action.outSub')
        }
        onPunch={() => setConfirming(action)}
      />
    );
  })();

  const confirmSite = home.openSession?.site ?? home.shift?.site ?? null;

  return (
    /*
      * One column, top to bottom, scrolling with the shell's `main` (C-19).
      * No fixed zones, no inner scroller, no landscape special case: sideways
      * or on a short viewport the page simply scrolls, which is the one
      * behaviour every phone user already knows how to read.
      */
    <div className="flex flex-col gap-3" data-testid="punch-home">
      {/*
        * A TEST hook, and therefore `aria-hidden`.
        *
        * `sr-only` does not mean hidden — it means invisible and still
        * announced. Without this, VoiceOver reads the raw state-machine name
        * out loud ("NOT_PUNCHED_IN_WINDOW_OPEN") to the one worker who cannot
        * see the screen it describes. The state is already announced properly,
        * in French, by the panel below.
        */}
      <span data-testid="home-state" aria-hidden="true" className="sr-only">
        {home.state}
      </span>

      <InstrumentPanel
        {...instrument}
        {...(panelFooter === null ? {} : { footer: panelFooter })}
        {...(home.state === 'NO_SHIFT_TODAY' && contextShift !== null
          ? { footerLabel: t('cap.nextShift') }
          : {})}
      />

      {home.state === 'CONFIRMED' && queue.lastConfirmed !== null ? (
        <ReconciliationPanel
          deviceRecordedAt={home.queuedPunch?.deviceRecordedAt ?? confirmedDeviceAt}
          serverRecordedAt={queue.lastConfirmed.recordedAt}
          timezone={zone}
          idempotentReplay={queue.lastConfirmed.idempotentReplay}
        />
      ) : null}

      {/*
        * The key comes RIGHT AFTER the state, before the tasks: punching is
        * the thirty-second job this screen exists for, and on a real phone
        * (~630 px of viewport behind the browser chrome) a task list above
        * the key scrolls it below the fold. The gated key still reads « N
        * tasks to do » with the titles directly beneath it.
        */}
      {actionSlot}

      {/*
        * Phase 5 / P3, T-14 — the open tasks, listed BY TITLE under their own
        * visible label. They do NOT stand down for `PunchOutGate`: the key
        * keeps its count and the rows keep their titles — redundancy a worker
        * benefits from.
        */}
      <HomeTasksLine tasks={tasks} />

      <div className="flex flex-col gap-2">
        {/*
          * ALWAYS quiet, on every state of this screen.
          *
          * It was drawn as a filled `--color-action` block wherever there was
          * no punch key — which is precisely the states a worker reaches when
          * something is already wrong, and the last place to put a control
          * that looks exactly like the punch key they have been trained to
          * press. A supervisor's name and number are information; information
          * does not wear the action colour, and a screen with nothing to press
          * is allowed to have nothing to press.
          *
          * The dead ends still end in a person — the line is the only control
          * on those screens rather than a second one — and when the API
          * reports nobody at all, the notice says so rather than showing a
          * link that opens an empty sheet.
          */}
        {(context?.supervisorContacts?.length ?? 0) > 0 ? (
          <SupervisorSheet
            contacts={context?.supervisorContacts}
            siteName={confirmSite?.name ?? home.nextShift?.site.name}
          />
        ) : actionSlot === null ? (
          <Notice
            tone="neutral"
            title={t('supervisor.contact')}
            description={t('supervisor.none')}
          />
        ) : null}
      </div>

      <PunchConfirmSheet
        action={confirming}
        onConfirm={() => {
          const type = confirming;
          setConfirming(null);
          if (type === 'in') onPunchIn();
          else if (type === 'out') onPunchOut();
        }}
        onCancel={() => setConfirming(null)}
        shift={home.shift}
        site={confirmSite}
        timezone={zone}
        locale={locale}
        now={now}
        elapsedMs={home.elapsedMs}
        offline={!queue.online}
      />
    </div>
  );
}
